// src/api/coupon/couponApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";

const BASE = "/v1/admin/coupons";

// Types
export type CouponType = "PUBLIC" | "PRIVATE" | "AUTO" | "EVENT" | "FREE_SHIPPING";
export type DiscountType = "FIXED" | "PERCENTAGE";
export type CouponScope = "CART" | "PRODUCT" | "COLLECTION";

export interface CouponListItem {
  couponId: number;
  couponName: string;
  couponCode: string;
  couponType: CouponType;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountAmount: number;
  minOrderAmount: number;
  maxOrderAmount: number;
  couponScope: CouponScope;
  issueLimit: number;
  currentIssueCount: number;
  canIssue: boolean;
  isActive: boolean;
  expiredAt: string;
  availableDay: number;
  createdAt: string;
}

export interface BrandInfo {
  brandId: number;
  brandName: string;
}

export interface CollectionInfo {
  collectionId: number;
  collectionName: string;
}

export interface ProductInfo {
  productId: number;
  productName: string;
}

export interface CouponDetail extends CouponListItem {
  applicableCollectionIds: string;
  applicableCollections: CollectionInfo[];
  applicableProductIds: string;
  applicableProducts: ProductInfo[];
  applicableBrandIds: string;
  applicableBrands: BrandInfo[];
  excludedCollectionIds: string;
  excludedCollections: CollectionInfo[];
  excludedProductIds: string;
  excludedProducts: ProductInfo[];
  excludedBrandIds: string;
  excludedBrands: BrandInfo[];
  description: string;
  createdId: string;
  updatedId: string;
  updatedAt: string;
}

export interface CouponListResponse {
  content: CouponListItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface GetCouponListParams {
  couponName?: string;
  couponCode?: string;
  couponType?: CouponType;
  couponScope?: CouponScope;
  isActive?: boolean;
  deleted?: boolean;
  page: number;
  size: number;
  order?: string;
}

export interface CouponUpsertPayload {
  couponName: string;
  couponCode: string;
  couponType: CouponType;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountAmount: number;
  minOrderAmount: number;
  maxOrderAmount: number;
  couponScope: CouponScope;
  applicableCollectionIds?: number[];
  applicableProductIds?: number[];
  applicableBrandIds?: number[];
  excludedCollectionIds?: number[];
  excludedProductIds?: number[];
  excludedBrandIds?: number[];
  issueLimit: number | null;
  expiredAt: string;
  availableDay: number;
  description?: string;
}

// API Functions

// 쿠폰 목록 조회
export const getCouponListApi = async (
  params: GetCouponListParams
): Promise<ApiResponse<CouponListResponse>> => {
  const res = await axiosInstance.get<ApiResponse<CouponListResponse>>(BASE, {
    params,
    snackbar: { success: false },
  });
  return res.data;
};

// 쿠폰 상세 조회
export const getCouponDetailApi = async (couponId: number): Promise<ApiResponse<CouponDetail>> => {
  const res = await axiosInstance.get<ApiResponse<CouponDetail>>(`${BASE}/${couponId}`, {
    snackbar: { success: false },
  });
  return res.data;
};

// 쿠폰 등록
export const createCouponApi = async (
  payload: CouponUpsertPayload
): Promise<ApiResponse<CouponDetail>> => {
  const res = await axiosInstance.post<ApiResponse<CouponDetail>>(BASE, payload);
  return res.data;
};

// 쿠폰 수정
export const updateCouponApi = async (
  couponId: number,
  payload: CouponUpsertPayload
): Promise<ApiResponse<CouponDetail>> => {
  const res = await axiosInstance.put<ApiResponse<CouponDetail>>(`${BASE}/${couponId}`, payload);
  return res.data;
};

// 쿠폰 삭제
export const deleteCouponApi = async (couponId: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`${BASE}/${couponId}`);
  return res.data;
};

// 쿠폰 활성화 상태 변경
export const updateCouponStatusApi = async (
  couponId: number,
  isActive: boolean
): Promise<ApiResponse<CouponDetail>> => {
  const res = await axiosInstance.patch<ApiResponse<CouponDetail>>(`${BASE}/${couponId}`, {
    isActive,
  });
  return res.data;
};

// 대량 발급 API 는 src/api/couponIssuance/couponIssuanceApi.ts 로 분리되었습니다.
